/**
 * Common visual formatters. Kept dumb and idempotent so they can be safely applied to data of
 * unknown shape (already-masked / unmasked / null).
 */

/**
 * Formats a CNPJ string as `XX.XXX.XXX/XXXX-YY`.
 * - Accepts already-masked values (returns them untouched).
 * - Returns the original string if it doesn't have exactly 14 digits.
 * - Returns `""` for null/undefined.
 */
/**
 * Formats a Brazilian phone number as `(DD) DDDD-DDDD` / `(DD) DDDDD-DDDD`.
 * - Strips a leading country code (+55) when it still leaves a full number.
 * - Partial input is masked progressively (usable as an input mask while typing).
 * - Returns the original string when it has more digits than a phone number; `""` for null/undefined.
 */
export function formatPhone(value: string | null | undefined): string {
  if (value == null) return "";
  let digits = value.replace(/\D/g, "");
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) digits = digits.slice(2);
  if (digits.length > 11) return value;
  if (digits.length <= 2) return digits.length ? `(${digits}` : "";
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  const split = rest.length <= 8 ? 4 : 5;
  return `(${ddd}) ${rest.slice(0, split)}-${rest.slice(split)}`;
}

export function formatCnpj(value: string | null | undefined): string {
  if (value == null) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 14) return value;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/**
 * Formats a numeric quantity for display, dropping insignificant trailing zeros so a whole number reads as
 * `4` (not `4.0000`) and a fractional one keeps only its meaningful digits (`4,72`). Uses pt-BR grouping and
 * decimal comma. Accepts the backend's plain-string decimals or a number; returns the original string
 * untouched when it isn't a finite number, and `""` for null/undefined.
 */
export function formatQuantity(value: string | number | null | undefined): string {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 4 });
}
